import { useMemo, useRef, useState } from 'react'
import { Loader2, RefreshCw, Trash2 } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { AdminUsersToolbar } from '@/components/custom/admin/admin-users-toolbar'
import {
  AdminUserTable,
  type AdminDirectoryRow,
} from '@/components/custom/admin/admin-user-table'
import { AssignCompanyModal } from '@/components/custom/admin/assign-company-modal'
import { EditGreybullUserSheet } from '@/components/custom/admin/edit-greybull-user-sheet'
import { InviteUserSheet } from '@/components/custom/admin/invite-user-sheet'
import { formatAllowedAppsSummary } from '@/components/custom/admin/user-app-access-fields'
import {
  type AdminUser,
  type InternalInvitation,
  useInternalInvitations,
  useResendInternalInvitation,
  useRevokeInternalInvitation,
} from '@/hooks/use-admin'
import { isOgsmEnabled } from '@/configs/feature-flags'
import { OGSM_ACCESS_COPY } from '@/features/ogsm/access'
import { initialsOf } from '@/lib/initials'
import { showError, showSuccess } from '@/lib/toast'
import { selectUser, useUserStore } from '@/stores/user.store'
import type { AllowedApp } from '@/types/api'

const INTERNAL_ROLE_OPTIONS = [
  { value: 'GREYBULL_ADMIN', label: 'Admin' },
  { value: 'HEAD_OF_PORTFOLIO', label: 'Head of Portfolio' },
  { value: 'GREYBULL_MEMBER', label: 'Member' },
]

const ROLE_LABELS = new Map(
  INTERNAL_ROLE_OPTIONS.map((option) => [option.value, option.label])
)

interface GreybullUsersTabProps {
  users: AdminUser[]
}

export function GreybullUsersTab({ users }: GreybullUsersTabProps) {
  const currentProfile = useUserStore(selectUser)
  const ogsmEnabled = isOgsmEnabled()
  const [search, setSearch] = useState('')
  const [role, setRole] = useState('all')
  const [appAccess, setAppAccess] = useState('all')
  const [status, setStatus] = useState('all')
  const [editingUserId, setEditingUserId] = useState<string | null>(null)
  const [assignCompanyUserId, setAssignCompanyUserId] = useState<string | null>(
    null
  )
  const [inviteOpen, setInviteOpen] = useState(false)
  const [revokeConfirm, setRevokeConfirm] = useState<InternalInvitation | null>(
    null
  )
  const inviteButtonRef = useRef<HTMLButtonElement>(null)
  const invitations = useInternalInvitations()
  const resendInvitation = useResendInternalInvitation()
  const revokeInvitation = useRevokeInternalInvitation()

  const internalUsers = useMemo(
    () => users.filter((user) => user.userType === 'INTERNAL'),
    [users]
  )
  const pendingInvitations = invitations.data?.data ?? []

  const rows = (() => {
    const query = search.trim().toLowerCase()
    const userRows: AdminDirectoryRow[] = internalUsers
      .filter((user) => {
        const matchesSearch =
          !query ||
          user.name.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query)
        return (
          matchesSearch &&
          (role === 'all' || user.role === role) &&
          matchesAppAccess(user.allowedApps, appAccess) &&
          (status === 'all' ||
            (status === 'active' && user.isActive) ||
            (status === 'inactive' && !user.isActive))
        )
      })
      .map((user) => {
        const activeAssignments = user.companyAssignments.filter(
          (assignment) => assignment.isActive
        )
        const isCurrentUser = user.id === currentProfile?.id
        const displayName = user.name || nameFromEmail(user.email)
        return {
          id: user.id,
          name: `${displayName}${isCurrentUser ? ' (you)' : ''}`,
          email: user.email,
          initials: initialsFor(displayName),
          role: ROLE_LABELS.get(user.role) ?? user.role,
          appAccess: formatAllowedAppsSummary(user.allowedApps),
          appAccessDetail:
            ogsmEnabled && user.ogsmCanEdit != null
              ? OGSM_ACCESS_COPY.rowLabel(user.ogsmCanEdit)
              : undefined,
          companies:
            activeAssignments
              .map((assignment) => assignment.companyName)
              .join(', ') || 'None',
          status: {
            kind: user.isActive ? 'active' : 'inactive',
            label: user.isActive ? 'Active' : 'Inactive',
          },
          activationLabel: `Edit ${displayName}`,
          onActivate: () => setEditingUserId(user.id),
        }
      })

    if (status !== 'all' && status !== 'invited') return userRows

    const invitationRows: AdminDirectoryRow[] = pendingInvitations
      .filter((invitation) => {
        const matchesSearch =
          !query || invitation.emailAddress.toLowerCase().includes(query)
        return (
          matchesSearch &&
          (role === 'all' || invitation.invitedRole === role) &&
          matchesAppAccess(invitation.allowedApps, appAccess)
        )
      })
      .map((invitation) => ({
        id: invitation.id,
        name: nameFromEmail(invitation.emailAddress),
        email: invitation.emailAddress,
        initials: initialsFor(invitation.emailAddress),
        role: invitation.invitedRole
          ? (ROLE_LABELS.get(invitation.invitedRole) ?? invitation.invitedRole)
          : 'Role not set',
        appAccess: formatAllowedAppsSummary(invitation.allowedApps),
        appAccessDetail:
          ogsmEnabled && invitation.ogsmAccess != null
            ? OGSM_ACCESS_COPY.rowLabel(invitation.ogsmAccess === 'EDIT')
            : undefined,
        companies: 'None',
        status: {
          kind: 'invited',
          label: invitation.isConfigured ? 'Invited' : 'Needs attention',
          detail: `Invited ${formatDate(invitation.createdAt)}`,
        },
        actions: (
          <>
            {invitation.isConfigured ? (
              <Button
                variant="outline"
                size="sm"
                disabled={
                  resendInvitation.isPending &&
                  resendInvitation.variables === invitation.id
                }
                onClick={() => void handleResend(invitation)}
              >
                {resendInvitation.isPending &&
                resendInvitation.variables === invitation.id ? (
                  <Loader2
                    className="size-3.5 animate-spin"
                    aria-hidden="true"
                  />
                ) : (
                  <RefreshCw className="size-3.5" aria-hidden="true" />
                )}
                Resend
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => setRevokeConfirm(invitation)}
            >
              <Trash2 className="size-3.5" aria-hidden="true" />
              Revoke
            </Button>
          </>
        ),
      }))

    return [...userRows, ...invitationRows]
  })()

  const editingUser = editingUserId
    ? (internalUsers.find((user) => user.id === editingUserId) ?? null)
    : null
  const assignCompanyUser = assignCompanyUserId
    ? (internalUsers.find((user) => user.id === assignCompanyUserId) ?? null)
    : null

  async function handleResend(invitation: InternalInvitation) {
    try {
      await resendInvitation.mutateAsync(invitation.id)
      showSuccess(`Invitation resent to ${invitation.emailAddress}`)
    } catch (error) {
      showError(error, 'Failed to resend invitation')
    }
  }

  async function handleRevoke() {
    if (!revokeConfirm) return
    try {
      await revokeInvitation.mutateAsync(revokeConfirm.id)
      showSuccess(`Invitation to ${revokeConfirm.emailAddress} revoked`)
      setRevokeConfirm(null)
    } catch (error) {
      showError(error, 'Failed to revoke invitation')
    }
  }

  const totalRows = internalUsers.length + pendingInvitations.length

  return (
    <>
      <div className="flex w-full max-w-7xl flex-col gap-5">
        <AdminUsersToolbar
          search={search}
          searchPlaceholder="Search people or email"
          onSearchChange={setSearch}
          role={role}
          roleOptions={INTERNAL_ROLE_OPTIONS}
          onRoleChange={setRole}
          appAccess={appAccess}
          onAppAccessChange={setAppAccess}
          status={status}
          statusOptions={[
            { value: 'all', label: 'All statuses' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'invited', label: 'Invited' },
          ]}
          onStatusChange={setStatus}
          onInvite={() => setInviteOpen(true)}
          inviteButtonRef={inviteButtonRef}
        />

        {invitations.isError ? (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3">
            <Typography variant="body-sm" className="text-destructive">
              Pending invitations could not be loaded.
            </Typography>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void invitations.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : null}

        <AdminUserTable
          rows={rows}
          showCompanies
          ariaLabel="Greybull team users and pending invitations"
          emptyMessage="No team members match these filters."
        />

        <Typography
          variant="body-sm"
          className="text-primary/70"
          aria-live="polite"
        >
          Showing {rows.length} of {totalRows} users and invitations
        </Typography>
      </div>

      <InviteUserSheet
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        mode="internal"
        returnFocusRef={inviteButtonRef}
        askOgsmAccess={ogsmEnabled}
      />

      {editingUser ? (
        <EditGreybullUserSheet
          open
          onOpenChange={(open) => {
            if (!open) setEditingUserId(null)
          }}
          user={editingUser}
          onAddCompanyClick={() => setAssignCompanyUserId(editingUser.id)}
          showOgsmAccess={ogsmEnabled}
        />
      ) : null}

      {assignCompanyUser ? (
        <AssignCompanyModal
          key={`${assignCompanyUser.id}-${assignCompanyUser.companyAssignments.length}`}
          open
          onOpenChange={(open) => {
            if (!open) setAssignCompanyUserId(null)
          }}
          user={assignCompanyUser}
        />
      ) : null}

      <AlertDialog
        open={revokeConfirm !== null}
        onOpenChange={(open) => {
          if (!open && !revokeInvitation.isPending) setRevokeConfirm(null)
        }}
      >
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke invitation?</AlertDialogTitle>
            <AlertDialogDescription>
              {revokeConfirm
                ? `The invitation to ${revokeConfirm.emailAddress} will stop working. You can send a new invitation later.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revokeInvitation.isPending}>
              Keep invitation
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={revokeInvitation.isPending}
              onClick={(event) => {
                event.preventDefault()
                void handleRevoke()
              }}
            >
              {revokeInvitation.isPending ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
              ) : null}
              Revoke invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function matchesAppAccess(allowedApps: AllowedApp[], filter: string) {
  if (filter === 'all') return true
  if (filter === 'none') return allowedApps.length === 0
  if (filter === 'both') {
    return allowedApps.includes('compass') && allowedApps.includes('ogsm')
  }
  return allowedApps.includes(filter === 'compass' ? 'compass' : 'ogsm')
}

function initialsFor(value: string) {
  return initialsOf(nameFromEmail(value))
}

function nameFromEmail(value: string) {
  const source = value.includes('@') ? value.split('@')[0] : value
  const normalized = source.replace(/[._+-]+/g, ' ').trim()
  return normalized
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => `${part[0]?.toUpperCase() ?? ''}${part.slice(1)}`)
    .join(' ')
}

function formatDate(timestamp: number) {
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}
